import { useCallback, useMemo } from 'react'

import { createProductId } from 'helpers/createProductId'
import { getAxonItem } from 'helpers/getAxonItem'

import { eventLogger } from 'services/eventLogger.service'
import { googleAnalyticsLogger } from 'services/googleAnalytics.service'

import { ScreenName } from 'root-constants'

import { usePurchaseStore } from './usePurchaseStore'

type TReturnPurchaseAnalytics = {
  sendGeneralPurchaseEvents: (shouldPurchaseShownBeLogged?: boolean) => void
}

export const usePurchaseAnalytics = (
  screenName: ScreenName = ScreenName.PURCHASE,
): TReturnPurchaseAnalytics => {
  const {
    uuid,
    periodName,
    periodQuantity,
    price,
    subscriptions,
    selectedSubscription,
    trialPrice,
    stripeAccountId,
    stripeAccountName,
  } = usePurchaseStore()

  const productId = useMemo(
    () =>
      createProductId({
        periodName,
        periodQuantity,
        price: trialPrice || price,
      }),
    [periodName, periodQuantity, trialPrice, price],
  )

  const sendGeneralPurchaseEvents = useCallback(
    (shouldPurchaseShownBeLogged = true) => {
      if (shouldPurchaseShownBeLogged) {
        eventLogger.logPurchaseShown({
          productId,
          screenName,
          stripeAccountId,
          stripeAccountName,
        })
      }

      window.fbq('track', 'InitiateCheckout', {}, { eventID: uuid })

      if (selectedSubscription && window.axon) {
        window.axon('track', 'add_to_cart', {
          value:
            selectedSubscription.trialPrices?.fullPrice ||
            selectedSubscription.mainPrices?.fullPrice,
          currency: selectedSubscription.currency.toUpperCase(),
          items: getAxonItem([selectedSubscription]),
        })

        window.axon('track', 'begin_checkout', {
          currency: selectedSubscription?.currency.toUpperCase(),
          value:
            selectedSubscription.trialPrices?.fullPrice ||
            selectedSubscription.mainPrices?.fullPrice,
          items: getAxonItem([selectedSubscription]),
        })
      }

      googleAnalyticsLogger.logCheckoutStarted(subscriptions)
    },
    [
      uuid,
      selectedSubscription,
      subscriptions,
      productId,
      screenName,
      stripeAccountId,
      stripeAccountName,
    ],
  )

  return { sendGeneralPurchaseEvents }
}
